import Card from '../components/ui/Card';
import Pagination from '../components/ui/Pagination';
import Modal from '../components/ui/Modal';
import Toast from '../components/ui/Toast';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { useState, useEffect, useCallback } from 'react';
import { fetchWithAdminAuth } from '../utils/adminAuth';

interface User {
  id: string;
  name: string;
  email: string;
  credits?: number;
  createdAt?: string;
}

interface UserDetail {
  id: string;
  name: string;
  email: string;
  credits: number;
  phone?: string;
  birthDate?: string;
  acceptsMarketing?: boolean;
  authType?: string;
  createdAt: string;
}

interface UserConversation {
  id: string;
  consultantName: string;
  status: string;
  duration: number;
  paidClient: number;
  startDate: string;
  endDate?: string | null;
  createdAt: string;
}

interface UserPayment {
  id: string;
  amount: number;
  paymentType: string;
  description?: string;
  status: string;
  mbReference?: string | null;
  mbEntity?: string | null;
  createdAt: string;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const PAGE_SIZE = 20;
const MODAL_PAGE_SIZE = 5;

type ModalSection = 'consultas' | 'pagamentos';

export default function Clientes() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [data, setData] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationData>({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });

  // Client detail modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [userDetail, setUserDetail] = useState<UserDetail | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<ModalSection>('consultas');

  // Modal conversations state
  const [conversations, setConversations] = useState<UserConversation[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [conversationsError, setConversationsError] = useState<string | null>(null);
  const [conversationsPagination, setConversationsPagination] = useState<PaginationData>({
    page: 1,
    limit: MODAL_PAGE_SIZE,
    total: 0,
    totalPages: 1,
  });

  // Modal payments state
  const [payments, setPayments] = useState<UserPayment[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsError, setPaymentsError] = useState<string | null>(null);
  const [paymentsPagination, setPaymentsPagination] = useState<PaginationData>({
    page: 1,
    limit: MODAL_PAGE_SIZE,
    total: 0,
    totalPages: 1,
  });

  // Modal filters
  const [conversationStatusFilter, setConversationStatusFilter] = useState('all');
  const [conversationStartDateFilter, setConversationStartDateFilter] = useState('');
  const [conversationEndDateFilter, setConversationEndDateFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('all');
  const [paymentStartDateFilter, setPaymentStartDateFilter] = useState('');
  const [paymentEndDateFilter, setPaymentEndDateFilter] = useState('');

  // Export/toast state
  const [exportingMarketing, setExportingMarketing] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 400);
    return () => clearTimeout(timer);
  }, [query]);

  // Reload when search or page changes
  useEffect(() => {
    loadUsers(1);
  }, [debouncedQuery]);

  const loadUsers = useCallback(async (page: number) => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      if (debouncedQuery) params.set('search', debouncedQuery);

      const response = await fetchWithAdminAuth(`/api/users?${params}`, { method: 'GET' });
      
      if (!response.ok) {
        setError('Erro ao carregar clientes');
        return;
      }
      
      const result = await response.json();
      setData(result.users || []);
      setPagination(result.pagination || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
    } catch (err) {
      console.error('Erro ao buscar usuários:', err);
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery]);

  const handlePageChange = (page: number) => {
    loadUsers(page);
  };

  const loadUserConversations = useCallback(async (userId: string, page: number) => {
    try {
      setConversationsLoading(true);
      setConversationsError(null);

      const params = new URLSearchParams({
        page: String(page),
        limit: String(MODAL_PAGE_SIZE),
      });
      if (conversationStatusFilter !== 'all') params.set('status', conversationStatusFilter);
      if (conversationStartDateFilter) params.set('startDate', conversationStartDateFilter);
      if (conversationEndDateFilter) params.set('endDate', conversationEndDateFilter);

      const response = await fetchWithAdminAuth(`/api/users/${userId}/conversations?${params.toString()}`);

      if (!response.ok) {
        throw new Error('Erro ao carregar consultas');
      }

      const result = await response.json();
      setConversations(result.conversations || []);
      setConversationsPagination(result.pagination || {
        page: 1,
        limit: MODAL_PAGE_SIZE,
        total: 0,
        totalPages: 1,
      });
    } catch (err) {
      console.error('Error loading user conversations:', err);
      setConversations([]);
      setConversationsError('Erro ao carregar consultas');
    } finally {
      setConversationsLoading(false);
    }
  }, [conversationEndDateFilter, conversationStartDateFilter, conversationStatusFilter]);

  const loadUserPayments = useCallback(async (userId: string, page: number) => {
    try {
      setPaymentsLoading(true);
      setPaymentsError(null);

      const params = new URLSearchParams({
        page: String(page),
        limit: String(MODAL_PAGE_SIZE),
      });
      if (paymentStatusFilter !== 'all') params.set('status', paymentStatusFilter);
      if (paymentTypeFilter !== 'all') params.set('paymentType', paymentTypeFilter);
      if (paymentStartDateFilter) params.set('startDate', paymentStartDateFilter);
      if (paymentEndDateFilter) params.set('endDate', paymentEndDateFilter);

      const response = await fetchWithAdminAuth(`/api/users/${userId}/payments?${params.toString()}`);

      if (!response.ok) {
        throw new Error('Erro ao carregar pagamentos');
      }

      const result = await response.json();
      setPayments(result.payments || []);
      setPaymentsPagination(result.pagination || {
        page: 1,
        limit: MODAL_PAGE_SIZE,
        total: 0,
        totalPages: 1,
      });
    } catch (err) {
      console.error('Error loading user payments:', err);
      setPayments([]);
      setPaymentsError('Erro ao carregar pagamentos');
    } finally {
      setPaymentsLoading(false);
    }
  }, [paymentEndDateFilter, paymentStartDateFilter, paymentStatusFilter, paymentTypeFilter]);

  const handleConversationsPageChange = (page: number) => {
    if (!selectedUserId) return;
    loadUserConversations(selectedUserId, page);
  };

  const handlePaymentsPageChange = (page: number) => {
    if (!selectedUserId) return;
    loadUserPayments(selectedUserId, page);
  };

  const handleExportMarketingEmails = async () => {
    try {
      setExportingMarketing(true);

      const response = await fetchWithAdminAuth('/api/users/export/marketing-emails');
      if (!response.ok) {
        throw new Error('Erro ao exportar emails');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `marketing-emails-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();

      setToast({ message: 'Exportação concluída com sucesso.', type: 'success' });
    } catch (err) {
      console.error('Error exporting marketing emails:', err);
      setToast({ message: 'Erro ao exportar emails de marketing.', type: 'error' });
    } finally {
      setExportingMarketing(false);
    }
  };

  useEffect(() => {
    if (!detailOpen || !selectedUserId || activeSection !== 'consultas') return;
    loadUserConversations(selectedUserId, 1);
  }, [activeSection, conversationEndDateFilter, conversationStartDateFilter, conversationStatusFilter, detailOpen, loadUserConversations, selectedUserId]);

  useEffect(() => {
    if (!detailOpen || !selectedUserId || activeSection !== 'pagamentos') return;
    loadUserPayments(selectedUserId, 1);
  }, [activeSection, detailOpen, loadUserPayments, paymentEndDateFilter, paymentStartDateFilter, paymentStatusFilter, paymentTypeFilter, selectedUserId]);

  const openUserDetail = async (userId: string) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setUserDetail(null);
    setSelectedUserId(userId);
    setActiveSection('consultas');

    setConversations([]);
    setConversationsError(null);
    setConversationsPagination({ page: 1, limit: MODAL_PAGE_SIZE, total: 0, totalPages: 1 });
    setConversationStatusFilter('all');
    setConversationStartDateFilter('');
    setConversationEndDateFilter('');

    setPayments([]);
    setPaymentsError(null);
    setPaymentsPagination({ page: 1, limit: MODAL_PAGE_SIZE, total: 0, totalPages: 1 });
    setPaymentStatusFilter('all');
    setPaymentTypeFilter('all');
    setPaymentStartDateFilter('');
    setPaymentEndDateFilter('');

    try {
      const userPromise = fetchWithAdminAuth(`/api/users/${userId}`);
      const conversationsPromise = loadUserConversations(userId, 1);
      const paymentsPromise = loadUserPayments(userId, 1);

      const [res] = await Promise.all([userPromise, conversationsPromise, paymentsPromise]);

      if (!res.ok) {
        throw new Error('Erro ao carregar ficha do cliente');
      }

      const userData = await res.json();
      setUserDetail(userData);
    } catch (err) {
      console.error('Error loading user detail:', err);
      setUserDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeUserDetail = () => {
    setDetailOpen(false);
    setUserDetail(null);
    setSelectedUserId(null);
    setConversations([]);
    setPayments([]);
  };

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-5">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={true}
          onClose={() => setToast(null)}
        />
      )}

      <Card
        title="Clientes"
        subtitle="Consulta rápida de clientes, saldo, sessões e consentimentos RGPD."
      >
        <div className="mb-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="relative w-full max-w-sm">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Pesquisar por nome ou email..."
              className="w-full px-3 py-2 rounded-lg bg-[#0b1220] border border-[#111827] text-[#e5e7eb] placeholder-[#64748b] focus:ring-2 focus:ring-[#8b5cf6] focus:border-transparent"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#e5e7eb]"
                aria-label="Limpar pesquisa"
              >
                ✕
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={handleExportMarketingEmails}
            disabled={exportingMarketing}
            className="h-10 px-4 rounded-lg border border-[#1f2937] bg-[#111827] text-[#e5e7eb] text-sm hover:bg-[#1f2937] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {exportingMarketing ? 'A exportar...' : 'Download emails marketing'}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <span className="ml-3 text-gray-400">A carregar...</span>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto w-full block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">Nome</TableHead>
                    <TableHead className="whitespace-nowrap">Email</TableHead>
                    <TableHead className="whitespace-nowrap">Créditos</TableHead>
                    <TableHead className="whitespace-nowrap">Registo</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="whitespace-nowrap">{row.name}</TableCell>
                      <TableCell className="font-mono text-[#94a3b8] whitespace-nowrap">{row.email}</TableCell>
                      <TableCell className="whitespace-nowrap">€ {(row.credits ?? 0).toFixed(2)}</TableCell>
                      <TableCell className="text-[#94a3b8] whitespace-nowrap">
                        {row.createdAt
                          ? new Date(row.createdAt).toLocaleDateString('pt-PT')
                          : '—'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <button
                          onClick={() => openUserDetail(row.id)}
                          className="text-[0.8rem] text-[#60a5fa] hover:underline"
                        >
                          Ver ficha
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                {data.length === 0 && (
                  <TableCaption>{error ? error : `Nenhum resultado para "${query}"`}</TableCaption>
                )}
              </Table>
            </div>

            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              totalItems={pagination.total}
              pageSize={pagination.limit}
              itemsOnPage={data.length}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </Card>

      {/* User Detail Modal */}
      <Modal
        isOpen={detailOpen}
        onClose={closeUserDetail}
        title="Ficha do Cliente"
      >
        {detailLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
          </div>
        ) : userDetail ? (
          <div className="space-y-4 max-h-[72vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[0.78rem]">
              <div>
                <span className="text-text-soft block">Nome</span>
                <span className="text-text font-medium">{userDetail.name}</span>
              </div>
              <div>
                <span className="text-text-soft block">Email</span>
                <span className="text-text font-medium text-xs">{userDetail.email}</span>
              </div>
              <div>
                <span className="text-text-soft block">Créditos</span>
                <span className="font-medium text-green-400">€ {(userDetail.credits ?? 0).toFixed(2)}</span>
              </div>
              <div>
                <span className="text-text-soft block">Telefone</span>
                <span className="text-text font-medium">{userDetail.phone || '—'}</span>
              </div>
              <div>
                <span className="text-text-soft block">Data de nascimento</span>
                <span className="text-text font-medium">
                  {userDetail.birthDate
                    ? new Date(userDetail.birthDate).toLocaleDateString('pt-PT')
                    : '—'}
                </span>
              </div>
              <div>
                <span className="text-text-soft block">Tipo de autenticação</span>
                <span className="text-text font-medium capitalize">{userDetail.authType || 'local'}</span>
              </div>
              <div>
                <span className="text-text-soft block">Marketing</span>
                <span className={`font-medium ${userDetail.acceptsMarketing ? 'text-green-400' : 'text-red-400'}`}>
                  {userDetail.acceptsMarketing ? 'Aceita' : 'Não aceita'}
                </span>
              </div>
              <div>
                <span className="text-text-soft block">Data de registo</span>
                <span className="text-text font-medium text-xs">
                  {new Date(userDetail.createdAt).toLocaleString('pt-PT')}
                </span>
              </div>
            </div>

            <div className="pt-1 border-t border-border-soft">
              <div className="flex items-center gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setActiveSection('consultas')}
                  className={`px-3 py-1.5 rounded-lg text-[0.76rem] border transition-colors ${
                    activeSection === 'consultas'
                      ? 'border-accent bg-accent/20 text-accent'
                      : 'border-border-soft text-text-soft hover:bg-white/5'
                  }`}
                >
                  Consultas feitas
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSection('pagamentos')}
                  className={`px-3 py-1.5 rounded-lg text-[0.76rem] border transition-colors ${
                    activeSection === 'pagamentos'
                      ? 'border-accent bg-accent/20 text-accent'
                      : 'border-border-soft text-text-soft hover:bg-white/5'
                  }`}
                >
                  Pagamentos
                </button>
              </div>

              {activeSection === 'consultas' ? (
                <div>
                  <div className="mb-3 grid grid-cols-1 md:grid-cols-3 gap-2">
                    <select
                      value={conversationStatusFilter}
                      onChange={(e) => setConversationStatusFilter(e.target.value)}
                      className="h-9 px-2.5 rounded-lg border border-border-soft bg-[#0a0b14]/80 text-text text-[0.78rem] outline-none"
                    >
                      <option value="all">Todos os estados</option>
                      <option value="active">Em curso</option>
                      <option value="completed">Terminada</option>
                      <option value="pending">Pendente</option>
                      <option value="cancelled">Cancelada</option>
                    </select>
                    <input
                      type="date"
                      value={conversationStartDateFilter}
                      onChange={(e) => setConversationStartDateFilter(e.target.value)}
                      className="h-9 px-2.5 rounded-lg border border-border-soft bg-[#0a0b14]/80 text-text text-[0.78rem] outline-none"
                    />
                    <input
                      type="date"
                      value={conversationEndDateFilter}
                      onChange={(e) => setConversationEndDateFilter(e.target.value)}
                      className="h-9 px-2.5 rounded-lg border border-border-soft bg-[#0a0b14]/80 text-text text-[0.78rem] outline-none"
                    />
                  </div>

                  <div className="rounded-xl border border-border-soft overflow-x-auto w-full block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="whitespace-nowrap">Data</TableHead>
                          <TableHead className="whitespace-nowrap">Consultor</TableHead>
                          <TableHead className="whitespace-nowrap">Duração</TableHead>
                          <TableHead className="whitespace-nowrap">Pago</TableHead>
                          <TableHead className="whitespace-nowrap">Estado</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {conversationsLoading ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-6 text-text-soft">
                              A carregar consultas...
                            </TableCell>
                          </TableRow>
                        ) : conversations.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-6 text-text-soft">
                              {conversationsError || 'Sem consultas para mostrar'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          conversations.map((conversation) => (
                            <TableRow key={conversation.id}>
                              <TableCell className="whitespace-nowrap">{new Date(conversation.startDate || conversation.createdAt).toLocaleString('pt-PT')}</TableCell>
                              <TableCell className="whitespace-nowrap">{conversation.consultantName || '—'}</TableCell>
                              <TableCell className="whitespace-nowrap">{Math.round((conversation.duration || 0) / 60)} min</TableCell>
                              <TableCell className="whitespace-nowrap">€ {(conversation.paidClient || 0).toFixed(2)}</TableCell>
                              <TableCell className="whitespace-nowrap">{conversation.status}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  <Pagination
                    currentPage={conversationsPagination.page}
                    totalPages={conversationsPagination.totalPages}
                    totalItems={conversationsPagination.total}
                    pageSize={conversationsPagination.limit}
                    itemsOnPage={conversations.length}
                    onPageChange={handleConversationsPageChange}
                  />
                </div>
              ) : (
                <div>
                  <div className="mb-3 grid grid-cols-1 md:grid-cols-4 gap-2">
                    <select
                      value={paymentStatusFilter}
                      onChange={(e) => setPaymentStatusFilter(e.target.value)}
                      className="h-9 px-2.5 rounded-lg border border-border-soft bg-[#0a0b14]/80 text-text text-[0.78rem] outline-none"
                    >
                      <option value="all">Todos os estados</option>
                      <option value="Paid">Pago</option>
                      <option value="Pending">Pendente</option>
                      <option value="Failed">Falhado</option>
                      <option value="Cancelled">Cancelado</option>
                    </select>
                    <select
                      value={paymentTypeFilter}
                      onChange={(e) => setPaymentTypeFilter(e.target.value)}
                      className="h-9 px-2.5 rounded-lg border border-border-soft bg-[#0a0b14]/80 text-text text-[0.78rem] outline-none"
                    >
                      <option value="all">Todos os métodos</option>
                      <option value="MBway">MBway</option>
                      <option value="RefMB">Referência MB</option>
                      <option value="Stripe">Stripe</option>
                    </select>
                    <input
                      type="date"
                      value={paymentStartDateFilter}
                      onChange={(e) => setPaymentStartDateFilter(e.target.value)}
                      className="h-9 px-2.5 rounded-lg border border-border-soft bg-[#0a0b14]/80 text-text text-[0.78rem] outline-none"
                    />
                    <input
                      type="date"
                      value={paymentEndDateFilter}
                      onChange={(e) => setPaymentEndDateFilter(e.target.value)}
                      className="h-9 px-2.5 rounded-lg border border-border-soft bg-[#0a0b14]/80 text-text text-[0.78rem] outline-none"
                    />
                  </div>

                  <div className="rounded-xl border border-border-soft overflow-x-auto w-full block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="whitespace-nowrap">Data</TableHead>
                          <TableHead className="whitespace-nowrap">Método</TableHead>
                          <TableHead className="whitespace-nowrap">Valor</TableHead>
                          <TableHead className="whitespace-nowrap">Estado</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paymentsLoading ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-6 text-text-soft">
                              A carregar pagamentos...
                            </TableCell>
                          </TableRow>
                        ) : payments.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-6 text-text-soft">
                              {paymentsError || 'Sem pagamentos para mostrar'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          payments.map((payment) => (
                            <TableRow key={payment.id}>
                              <TableCell className="whitespace-nowrap">{new Date(payment.createdAt).toLocaleString('pt-PT')}</TableCell>
                              <TableCell className="whitespace-nowrap">{payment.paymentType}</TableCell>
                              <TableCell className="whitespace-nowrap">€ {(payment.amount || 0).toFixed(2)}</TableCell>
                              <TableCell className="whitespace-nowrap">{payment.status}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  <Pagination
                    currentPage={paymentsPagination.page}
                    totalPages={paymentsPagination.totalPages}
                    totalItems={paymentsPagination.total}
                    pageSize={paymentsPagination.limit}
                    itemsOnPage={payments.length}
                    onPageChange={handlePaymentsPageChange}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-text-soft">Utilizador não encontrado</div>
        )}
      </Modal>
    </div>
  );
}
